import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, ArrowLeft, Calendar, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StoryDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStory();
  }, [slug]);

  const fetchStory = async () => {
    try {
      const response = await axios.get(`${API}/stories/slug/${slug}`);
      setStory(response.data.data);
    } catch (err) {
      toast.error('Story not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: story.title || story.startup_name,
        text: story.short_description,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!story) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Story not found</h2>
          <Button onClick={() => navigate('/')}>Go to Homepage</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Bar */}
      <nav className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
            <Button variant="outline" onClick={handleShare}>
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
          </div>
        </div>
      </nav>

      {/* Story Content */}
      <article className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-4xl">
        {/* Header */}
        <header className="mb-8 text-center">
          <Badge className="mb-4">{story.submission_type || 'Startup Story'}</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold font-heading mb-4">
            {story.title || story.startup_name}
          </h1>
          <p className="text-xl text-muted-foreground mb-4">
            {story.short_description}
          </p>
          <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{formatDate(story.submitted_at || story.created_at)}</span>
            </div>
            <span>•</span>
            <span>{story.startup_name}</span>
          </div>
        </header>

        {/* Banner Photo */}
        {story.banner_photo && (
          <div className="mb-12 rounded-2xl overflow-hidden shadow-xl">
            <img 
              src={story.banner_photo} 
              alt={story.title || story.startup_name}
              className="w-full h-96 object-cover"
            />
          </div>
        )}

        {/* Founder Profiles */}
        {story.founder_profiles && story.founder_profiles.length > 0 && (
          <div className="mb-12 py-8 border-y border-border">
            <h2 className="text-2xl font-bold text-center mb-6">Founded By</h2>
            <div 
              className="grid gap-8 justify-items-center"
              style={{ gridTemplateColumns: `repeat(${Math.min(story.founder_profiles.length, 3)}, 1fr)` }}
            >
              {story.founder_profiles.map((profile, index) => (
                <div key={index} className="text-center flex flex-col items-center">
                  {profile.profile_picture && (
                    <>
                      <img 
                        src={profile.profile_picture} 
                        alt={profile.founder_name || 'Founder'}
                        className="w-40 h-40 object-cover rounded-xl border-4 border-primary shadow-lg mb-3"
                      />
                      <p className="font-semibold text-lg">
                        {profile.founder_name || 'Founder'}
                      </p>
                      {profile.founder_designation && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {profile.founder_designation}
                        </p>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Story Content Based on Type */}
        <div className="prose prose-lg max-w-none text-left story-content">
          {(story.submission_type === 'Self Written' || story.submission_type === 'Expert Written') && story.article_content ? (
            // Self Written or Expert Written Article
            <div dangerouslySetInnerHTML={{ __html: story.article_content }} />
          ) : (
            // Questionnaire Format - Show Full Questions
            <div className="space-y-8">
              {story.brief_info && (
                <section>
                  <h3 className="text-xl font-bold mb-3 text-primary">
                    Give a brief info about your startup?
                  </h3>
                  <p className="text-lg leading-relaxed text-foreground whitespace-pre-wrap">
                    {story.brief_info}
                  </p>
                </section>
              )}

              {story.startup_journey && (
                <section>
                  <h3 className="text-xl font-bold mb-3 text-primary">
                    What made you start your journey and what problem does it solve?
                  </h3>
                  <p className="text-lg leading-relaxed text-foreground whitespace-pre-wrap">
                    {story.startup_journey}
                  </p>
                </section>
              )}

              {story.founder_background && (
                <section>
                  <h3 className="text-xl font-bold mb-3 text-primary">
                    Tell us about yourself, your previous jobs/ventures? What were you doing before this startup?
                  </h3>
                  <p className="text-lg leading-relaxed text-foreground whitespace-pre-wrap">
                    {story.founder_background}
                  </p>
                </section>
              )}

              {story.location_reason && (
                <section>
                  <h3 className="text-xl font-bold mb-3 text-primary">
                    Where is your startup based out of? Why do you think that is the best place for you?
                  </h3>
                  <p className="text-lg leading-relaxed text-foreground whitespace-pre-wrap">
                    {story.location_reason}
                  </p>
                </section>
              )}

              {story.founder_paranoia && (
                <section>
                  <h3 className="text-xl font-bold mb-3 text-primary">
                    As a startup founder, what are you paranoid about? What keeps you awake at night?
                  </h3>
                  <p className="text-lg leading-relaxed text-foreground whitespace-pre-wrap">
                    {story.founder_paranoia}
                  </p>
                </section>
              )}

              {story.competition_edge && (
                <section>
                  <h3 className="text-xl font-bold mb-3 text-primary">
                    Who are your competitions and how are you better than them?
                  </h3>
                  <p className="text-lg leading-relaxed text-foreground whitespace-pre-wrap">
                    {story.competition_edge}
                  </p>
                </section>
              )}

              {story.work_life_balance && (
                <section>
                  <h3 className="text-xl font-bold mb-3 text-primary">
                    How hard is it to have a work-life balance as a startup founder and how do you manage it?
                  </h3>
                  <p className="text-lg leading-relaxed text-foreground whitespace-pre-wrap">
                    {story.work_life_balance}
                  </p>
                </section>
              )}

              {story.funding_details && (
                <section>
                  <h3 className="text-xl font-bold mb-3 text-primary">
                    Have you raised funding? If yes, then we would like to know the details. If no then please tell us if you are looking to raise.
                  </h3>
                  <p className="text-lg leading-relaxed text-foreground whitespace-pre-wrap">
                    {story.funding_details}
                  </p>
                </section>
              )}

              {story.company_excitement && (
                <section>
                  <h3 className="text-xl font-bold mb-3 text-primary">
                    What gets you excited about this company?
                  </h3>
                  <p className="text-lg leading-relaxed text-foreground whitespace-pre-wrap">
                    {story.company_excitement}
                  </p>
                </section>
              )}

              {story.daily_schedule && (
                <section>
                  <h3 className="text-xl font-bold mb-3 text-primary">
                    Tell us how a day in your life looks like? Your schedule for a day right from the time you get up till you hit the bed at night.
                  </h3>
                  <p className="text-lg leading-relaxed text-foreground whitespace-pre-wrap">
                    {story.daily_schedule}
                  </p>
                </section>
              )}

              {story.team_info && (
                <section>
                  <h3 className="text-xl font-bold mb-3 text-primary">
                    Tell us about your team and how did you meet each other?
                  </h3>
                  <p className="text-lg leading-relaxed text-foreground whitespace-pre-wrap">
                    {story.team_info}
                  </p>
                </section>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-border text-center">
          <p className="text-muted-foreground mb-4">
            Enjoyed this story? Share it with your network!
          </p>
          <Button onClick={handleShare} className="mb-8">
            <Share2 className="h-4 w-4 mr-2" />
            Share This Story
          </Button>
          <div>
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to All Stories
            </Button>
          </div>
        </footer>
      </article>
    </div>
  );
}
